import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { updateUserTheme } from '../services/api';
import { getStoredValue, removeStoredValue, setStoredValue } from '../utils/storage';

// ThemeStateContext — changes when theme changes; consumed only by UI that displays the theme
const ThemeStateContext = createContext();

// ThemeActionsContext — stable forever; consumed by AuthContext so it never re-renders on theme change
const ThemeActionsContext = createContext();

export const useTheme = () => useContext(ThemeStateContext);
export const useThemeActions = () => useContext(ThemeActionsContext);

export const THEMES = [
  { id: 'saffron',    label: 'Saffron (Default)', description: 'Warm orange and amber tones' },
  { id: 'blue-dark',  label: 'Blue Dark',          description: 'Professional navy blue' },
  { id: 'emerald',    label: 'Emerald',             description: 'Accounting green' },
  { id: 'black',      label: 'Black',               description: 'Pure dark mode' },
];

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(
    () => getStoredValue('theme', 'saffron')
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const applyTheme = useCallback((themeId) => {
    if (themeId) {
      setStoredValue('theme', themeId);
    } else {
      removeStoredValue('theme');
    }
    setThemeState(themeId);
    document.documentElement.setAttribute('data-theme', themeId);
  }, []);

  const setTheme = useCallback((themeId) => {
    applyTheme(themeId);
    try {
      if (getStoredValue('user')) {
        updateUserTheme(themeId).catch(() => {});
      }
    } catch (_) {}
  }, [applyTheme]);

  // Stable actions object — useCallback guarantees these references never change
  const actions = useMemo(() => ({ setTheme, applyTheme }), [setTheme, applyTheme]);

  // State object — changes with theme, re-renders only ThemeStateContext consumers
  const state = useMemo(() => ({ theme, setTheme, applyTheme, themes: THEMES }), [theme, setTheme, applyTheme]);

  return (
    <ThemeActionsContext.Provider value={actions}>
      <ThemeStateContext.Provider value={state}>
        {children}
      </ThemeStateContext.Provider>
    </ThemeActionsContext.Provider>
  );
};
